import { useEffect, useState } from 'react';
import { apiClient } from '@copro/api';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { useGetSenderProvider } from '../../api/useGetSenderProvider';
import type { InboxEmail, SuggestedAction } from '../../types/inbox.types';
import { cleanEmailBodyText } from '../../utils/cleanEmailContent';

type Props = {
  open: boolean;
  onClose: () => void;
  email: InboxEmail | null;
  spaceId: string;
  initialSubTopicId?: string | null;
  onSuccess: () => void;
};

/** Provider named by the AI analysis of the e-mail, when the sender's address is unknown. */
export function getSuggestedProviderName(actions: SuggestedAction[]): string | null {
  for (const action of actions) {
    switch (action.type) {
      case 'CREATE_DOCUMENT':
      case 'CREATE_EVENT':
      case 'CREATE_PENDING_ITEM':
        if (action.suggestedProviderName?.trim()) return action.suggestedProviderName.trim();
        break;
      case 'CREATE_PROVIDER_CONTACT':
        if (action.providerName?.trim()) return action.providerName.trim();
        break;
      case 'CREATE_PROVIDER':
        if (action.name?.trim()) return action.name.trim();
        break;
      default:
        break;
    }
  }
  return null;
}

export function useCreateCommunicationFromInboxModal(props: Props) {
  const [subTopicId, setSubTopicId] = useState('');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  // null until edited: the field shows the suggestion, which may arrive after opening.
  const [editedProviderName, setEditedProviderName] = useState<string | null>(null);
  const [editedContactName, setEditedContactName] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: senderProvider } = useGetSenderProvider({
    spaceId: props.spaceId,
    fromEmail: props.email?.from_email,
    enabled: props.open,
  });

  useEffect(() => {
    if (props.open && props.email) {
      setSubTopicId(props.initialSubTopicId || props.email.matched_sub_topic_id || '');
      setTitle(props.email.subject || '');
      setContent(cleanEmailBodyText(props.email.body_text));
      setEditedProviderName(null);
      setEditedContactName(null);
    }
  }, [props.open, props.email, props.initialSubTopicId]);

  const suggestedProviderName =
    senderProvider?.name ?? (props.email ? getSuggestedProviderName(props.email.parsedActions) : null) ?? '';
  const suggestedContactName = props.email?.from_name?.trim() || props.email?.from_email || '';

  const providerName = editedProviderName ?? suggestedProviderName;
  const contactName = editedContactName ?? suggestedContactName;

  // Link the communication to the provider record, so it also shows on the provider page.
  async function resolveProviderId(name: string): Promise<string | null> {
    if (senderProvider && senderProvider.name === name) {
      return senderProvider.id;
    }

    const { data } = await apiClient
      .from('providers')
      .select('id')
      .eq('space_id', props.spaceId)
      .ilike('name', name)
      .limit(1);

    return data?.[0]?.id ?? null;
  }

  async function handleConfirm() {
    if (!subTopicId) {
      enqueueSnackbar('Veuillez sélectionner un sujet associé.', 'warning');
      return;
    }

    if (!title.trim()) {
      enqueueSnackbar('Le titre de la communication est obligatoire.', 'warning');
      return;
    }

    setIsSubmitting(true);

    try {
      const trimmedProviderName = providerName.trim();
      const providerId = trimmedProviderName ? await resolveProviderId(trimmedProviderName) : null;

      const { error } = await apiClient.from('communications').insert({
        space_id: props.spaceId,
        subject_id: subTopicId,
        title: title.trim(),
        comment: content.trim() || null,
        date: props.email?.received_at || new Date().toISOString(),
        type: 'email',
        provider_id: providerId,
        provider_name: trimmedProviderName || null,
        contact_name: contactName.trim() || null,
      });

      if (error) throw error;

      enqueueSnackbar('Communication ajoutée avec succès au sujet !', 'success');
      props.onSuccess();
      props.onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de l’ajout de la communication';
      enqueueSnackbar(msg, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }

  return {
    subTopicId,
    setSubTopicId,
    title,
    setTitle,
    content,
    setContent,
    providerName,
    setProviderName: setEditedProviderName,
    contactName,
    setContactName: setEditedContactName,
    isSubmitting,
    handleConfirm,
  };
}
